import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { leerCSV, leerXLSX, leerPegado, adivinarColumnas, revisarFilas } from '../utils/importar';

// Pasar una cartera entera de una hoja de cálculo al programa.
//
// Quien compra el programa llega con su cartera en Excel o en un cuaderno.
// Teclear doscientos clientes a mano es medio día, y es justo donde se
// abandona: la herramienta no sirve de nada hasta que los datos están dentro.
//
// El archivo se lee AQUÍ, en el navegador, y no se guarda nada hasta que una
// persona vea la tabla y diga que sí. Un importador que guarda solo es un
// importador que mete basura sin que nadie se entere.

const moneda = (n) => (n == null ? '—' : '$' + Math.round(n).toLocaleString('es-CO'));

const CAJA = {
  background: 'white', borderRadius: '10px', padding: '18px',
  boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: '16px',
};
const BOTON = {
  padding: '10px 18px', borderRadius: '7px', border: 'none',
  background: '#15803d', color: 'white', fontWeight: 700,
  fontSize: '15px', cursor: 'pointer',
};

const PESTANA = {
  padding: '9px 16px', borderRadius: '7px', border: '1px solid #d1d5db',
  background: 'white', color: '#4b5563', cursor: 'pointer', fontSize: '14px',
};
const PESTANA_ACTIVA = {
  background: '#eef2ff', borderColor: '#c7d2fe', color: '#4338ca', fontWeight: 700,
};

const CAMPOS = [
  ['name', 'Nombre'],
  ['cedula', 'Cédula'],
  ['phone', 'Teléfono'],
  ['address', 'Dirección'],
  ['amount', 'Monto prestado'],
  ['interest_rate', 'Interés (%)'],
  ['start_date', 'Fecha del préstamo'],
  ['maturity_date', 'Vencimiento'],
];

function Importar({ ajustes, onRefresh }) {
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [encabezados, setEncabezados] = useState(null);
  const [filas, setFilas] = useState([]);
  const [mapa, setMapa] = useState({});
  const [yaExisten, setYaExisten] = useState([]);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [pegando, setPegando] = useState(false);
  const [texto, setTexto] = useState('');

  const interesPorDefecto = (ajustes && ajustes.interest_rate) || 10;

  const revisadas = useMemo(
    () => revisarFilas(filas, mapa, { interesPorDefecto }),
    [filas, mapa, interesPorDefecto]);

  const existentes = new Set(yaExisten);
  const buenas = revisadas.filter(r => r.errores.length === 0 && !existentes.has(r.datos.cedula));
  const paradas = revisadas.filter(r => r.errores.length > 0);
  const repetidas = revisadas.filter(r => r.errores.length === 0 && existentes.has(r.datos.cedula));

  // De filas leídas a tabla revisada. Da igual de dónde vengan —de un CSV, de
  // un Excel o de un pegado—: a partir de aquí el camino es el mismo.
  const preparar = async (leidas) => {
    if (leidas.length < 2) {
      setError('El archivo no tiene filas debajo del encabezado.');
      setEncabezados(null); setFilas([]);
      return;
    }
    const cabecera = leidas[0];
    const cuerpo = leidas.slice(1);
    const adivinado = adivinarColumnas(cabecera);
    setEncabezados(cabecera);
    setFilas(cuerpo);
    setMapa(adivinado);

    // Se pregunta de una vez cuáles ya están, para que se vea antes de
    // guardar y no a mitad de camino.
    const cedulas = revisarFilas(cuerpo, adivinado)
      .map(r => r.datos.cedula).filter(Boolean);
    if (cedulas.length) {
      try {
        const { data } = await axios.post('/api/importar/comprobar', { cedulas });
        setYaExisten(data?.existen || []);
      } catch { /* si falla, el servidor los omite igual al guardar */ }
    }
  };

  const abrir = async (evento) => {
    const archivo = evento.target.files && evento.target.files[0];
    if (!archivo) return;
    setError(''); setResultado(null); setYaExisten([]); setPegando(false);
    setNombreArchivo(archivo.name);

    try {
      // El .xls viejo (el de antes de 2007) es otro formato distinto y ése sí
      // necesitaría una librería. Se dice, en vez de fallar con un error raro.
      if (/\.xls$/i.test(archivo.name)) { setError('xls-viejo'); return; }
      const leidas = /\.xlsx$/i.test(archivo.name)
        ? await leerXLSX(await archivo.arrayBuffer())
        : leerCSV(await archivo.text());
      await preparar(leidas);
    } catch (e) {
      setEncabezados(null); setFilas([]);
      setError('No se pudo leer el archivo: ' + e.message);
    }
  };

  const usarPegado = async () => {
    setError(''); setResultado(null); setYaExisten([]);
    setNombreArchivo('lo que pegó');
    try { await preparar(leerPegado(texto)); }
    catch (e) { setError('No se pudo leer lo que pegó: ' + e.message); }
  };

  const guardar = async () => {
    if (!buenas.length) return;
    if (!window.confirm(
      `Se van a agregar ${buenas.length} cliente(s)` +
      `${buenas.filter(b => b.conPrestamo).length
        ? ` y ${buenas.filter(b => b.conPrestamo).length} préstamo(s)` : ''}.\n\n` +
      `Las ${paradas.length} fila(s) con problemas no se guardan.\n\n¿Seguimos?`
    )) return;

    setGuardando(true);
    try {
      const { data } = await axios.post('/api/importar', {
        filas: buenas.map(b => ({ ...b.datos, frequency: 'monthly' })),
      });
      setResultado(data);
      setEncabezados(null); setFilas([]);
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || ('No se pudo guardar: ' + err.message));
    }
    setGuardando(false);
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>📂 Pasar mi cartera</h2>
      <p style={{ color: '#6b7280', marginTop: '-8px' }}>
        Si ya tiene sus clientes en una hoja de cálculo, no hay que teclearlos
      </p>

      {resultado && (
        <div style={{ ...CAJA, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <strong style={{ color: '#166534', fontSize: '17px' }}>
            ✅ Listo: {resultado.clientes} cliente{resultado.clientes === 1 ? '' : 's'}
            {resultado.prestamos > 0 &&
              ` y ${resultado.prestamos} préstamo${resultado.prestamos === 1 ? '' : 's'}`}
          </strong>
          {resultado.omitidas?.length > 0 && (
            <div style={{ fontSize: '13px', color: '#4b5563', marginTop: '8px' }}>
              Se saltaron {resultado.omitidas.length} que ya estaban registrados:{' '}
              {resultado.omitidas.slice(0, 5).map(o => o.name).join(', ')}
              {resultado.omitidas.length > 5 && ` y ${resultado.omitidas.length - 5} más`}.
            </div>
          )}
          {/* Las filas que no traían tasa. Se dice SIEMPRE y en amarillo.
              Antes esas filas quedaban al 0% sin avisar, y un préstamo al 0%
              no falla ni se ve raro: sale normal en todas las pantallas, con
              su saldo y sus abonos. Simplemente no cobra. En una sola cartera
              había 21 así, $83.860.000. */}
          {resultado.sinTasa > 0 && (
            <div style={{ fontSize: '14px', marginTop: '10px', padding: '10px 12px',
                          background: '#fffbeb', border: '1px solid #fde68a',
                          borderRadius: '6px', color: '#92400e' }}>
              <strong>
                {resultado.sinTasa === 1
                  ? '1 préstamo no traía el interés en el archivo'
                  : `${resultado.sinTasa} préstamos no traían el interés en el archivo`}
              </strong>
              {' — '}
              {resultado.sinTasa === 1 ? 'quedó' : 'quedaron'} al{' '}
              <strong>{resultado.tasaPorDefecto}%</strong>, que es el de su
              configuración. Revíselos en la lista de préstamos y corrija los
              que vayan a otra tasa.
            </div>
          )}
        </div>
      )}

      <div style={CAJA}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <button onClick={() => setPegando(false)}
                  style={{ ...PESTANA, ...(pegando ? {} : PESTANA_ACTIVA) }}>
            📄 Subir el archivo
          </button>
          <button onClick={() => setPegando(true)}
                  style={{ ...PESTANA, ...(pegando ? PESTANA_ACTIVA : {}) }}>
            📋 Pegar desde Excel
          </button>
        </div>

        {pegando ? (
          <>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>
              Copie las celdas en Excel y péguelas aquí
            </label>
            <textarea value={texto} onChange={e => setTexto(e.target.value)}
                      placeholder={'Nombre\tCédula\tTeléfono\tMonto\n' +
                                   'Jose Calabria\t1045220890\t3104459982\t5.480.000'}
                      rows={7}
                      style={{ width: '100%', padding: '10px', borderRadius: '7px',
                               border: '1px solid #d1d5db', fontSize: '13px',
                               fontFamily: 'monospace' }} />
            <button onClick={usarPegado} disabled={!texto.trim()}
                    style={{ ...BOTON, marginTop: '10px',
                             opacity: texto.trim() ? 1 : 0.5 }}>
              Revisar lo que pegó
            </button>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
              Incluya la fila de los títulos. Es el camino más corto: ni guardar
              el archivo ni buscarlo.
            </div>
          </>
        ) : (
          <>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>
              Elija su archivo
            </label>
            <input type="file" accept=".csv,.txt,.xlsx,.xls" onChange={abrir} />
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
              Excel (.xlsx) o CSV. No hace falta ninguna plantilla: sirve el
              archivo que ya tenga.
            </div>
            {nombreArchivo && (
              <div style={{ fontSize: '13px', color: '#374151', marginTop: '8px',
                            fontWeight: 600 }}>
                {nombreArchivo}
              </div>
            )}
          </>
        )}

        {/* El .xls de antes de 2007 es otro formato y ése sí necesitaría una
            librería. Se dice con el camino de salida, en vez de fallar con un
            error que no ayuda a nadie. */}
        {error === 'xls-viejo' ? (
          <div style={{
            marginTop: '12px', padding: '14px', background: '#fffbeb',
            border: '1px solid #fde68a', borderRadius: '8px', fontSize: '14px',
          }}>
            <strong>Ese es un Excel antiguo (.xls).</strong>
            <div style={{ marginTop: '6px', color: '#4b5563' }}>
              Ábralo en Excel y guárdelo como <strong>.xlsx</strong> o{' '}
              <strong>CSV</strong> — o copie las celdas y use{' '}
              <strong>Pegar desde Excel</strong>, aquí arriba.
            </div>
          </div>
        ) : error ? (
          <div style={{ marginTop: '12px', color: '#b91c1c', fontSize: '14px' }}>{error}</div>
        ) : null}

        {!encabezados && !resultado && !pegando && (
          <div style={{ marginTop: '14px', fontSize: '13px', color: '#6b7280' }}>
            El archivo puede tener las columnas en cualquier orden y con los
            nombres que usted use —nombre, cliente, cédula, cc, celular, valor
            prestado, tasa…—. Aquí se reconocen solas, y si alguna no se
            reconoce la elige usted.
          </div>
        )}
      </div>

      {encabezados && (
        <>
          <div style={CAJA}>
            <h3 style={{ marginTop: 0, fontSize: '16px' }}>¿Qué es cada columna?</h3>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              {CAMPOS.map(([campo, rotulo]) => (
                <div key={campo} style={{ minWidth: '11rem' }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280',
                                  marginBottom: '4px', fontWeight: 600 }}>
                    {rotulo}
                  </label>
                  <select
                    value={mapa[campo] === undefined ? '' : mapa[campo]}
                    onChange={e => setMapa(m => {
                      const n = { ...m };
                      if (e.target.value === '') delete n[campo];
                      else n[campo] = Number(e.target.value);
                      return n;
                    })}
                    style={{ width: '100%', padding: '7px 9px', borderRadius: '6px',
                             border: '1px solid #d1d5db', fontSize: '14px' }}>
                    <option value="">— no está —</option>
                    {encabezados.map((h, i) => (
                      <option key={i} value={i}>{h || `columna ${i + 1}`}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...CAJA, display: 'flex', gap: '20px', flexWrap: 'wrap',
                        alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#15803d' }}>
                {buenas.length}
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>listos para entrar</div>
            </div>
            {repetidas.length > 0 && (
              <div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#b45309' }}>
                  {repetidas.length}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>ya los tiene</div>
              </div>
            )}
            {paradas.length > 0 && (
              <div>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#b91c1c' }}>
                  {paradas.length}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>con problemas</div>
              </div>
            )}
            <button onClick={guardar} disabled={guardando || buenas.length === 0}
                    style={{ ...BOTON, marginLeft: 'auto',
                             opacity: (guardando || !buenas.length) ? 0.5 : 1 }}>
              {guardando ? 'Guardando…' : `Agregar ${buenas.length} a mis clientes`}
            </button>
          </div>

          <div style={{ ...CAJA, overflowX: 'auto', maxHeight: '32rem', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px' }}>#</th>
                  <th style={{ padding: '6px 8px' }}>Nombre</th>
                  <th style={{ padding: '6px 8px' }}>Cédula</th>
                  <th style={{ padding: '6px 8px' }}>Teléfono</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Monto</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Interés</th>
                  <th style={{ padding: '6px 8px' }}>Fecha</th>
                  <th style={{ padding: '6px 8px' }}>Qué pasa</th>
                </tr>
              </thead>
              <tbody>
                {revisadas.map(r => {
                  const repetido = existentes.has(r.datos.cedula);
                  const malo = r.errores.length > 0;
                  return (
                    <tr key={r.fila} style={{
                      background: malo ? '#fef2f2' : repetido ? '#fffbeb' : 'transparent',
                      borderTop: '1px solid #f3f4f6',
                    }}>
                      <td style={{ padding: '6px 8px', color: '#9ca3af' }}>{r.fila + 1}</td>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.datos.name || '—'}</td>
                      <td style={{ padding: '6px 8px' }}>{r.datos.cedula || '—'}</td>
                      <td style={{ padding: '6px 8px' }}>{r.datos.phone || '—'}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right',
                                   fontVariantNumeric: 'tabular-nums' }}>
                        {moneda(r.datos.amount)}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        {r.datos.interest_rate == null ? '—' : r.datos.interest_rate + '%'}
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {r.datos.start_date || '—'}
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                        {malo && (
                          <span style={{ color: '#b91c1c', fontWeight: 600 }}>
                            {r.errores.join(' · ')}
                          </span>
                        )}
                        {!malo && repetido && (
                          <span style={{ color: '#b45309' }}>ya lo tiene registrado</span>
                        )}
                        {!malo && !repetido && r.avisos.length > 0 && (
                          <span style={{ color: '#6b7280' }}>{r.avisos.join(' · ')}</span>
                        )}
                        {!malo && !repetido && r.avisos.length === 0 && (
                          <span style={{ color: '#15803d' }}>✓</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p style={{ fontSize: '13px', color: '#6b7280' }}>
            Las filas en rojo no se guardan: arréglelas en su archivo y vuelva a
            elegirlo. Las amarillas ya están en el programa y se saltan — lo que
            usted ya tiene aquí no se pisa con lo del archivo, que puede estar
            viejo.
          </p>
        </>
      )}
    </div>
  );
}

export default Importar;
